import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@osteopro/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * La confirmation du produit : suppression d'un rendez-vous, régénération d'un lien public,
 * effacement d'un compte. Une dizaine de dialogues identiques à un libellé près — un seul
 * composant.
 *
 * **Sans `onConfirm`, c'est un avertissement** : il explique et ne propose que de fermer. C'est la
 * forme d'un refus qu'aucun bouton ne lève — un créneau déjà pris, depuis le 30 septembre 2026 —,
 * où un « confirmer quand même » mentirait sur ce que le serveur fera.
 *
 * Il vit ici et non dans un module métier : la facturation, les dossiers, les paramètres et
 * l'agenda l'utilisent tous, et un composant partagé rangé chez l'un d'eux finit par lui
 * emprunter ses dépendances.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  details,
  confirmLabel,
  onConfirm,
  testId,
  confirmTestId,
  cancelTestId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /**
   * Détail sous la description : la liste des conflits d'un déplacement, par exemple. Hors de la
   * description parce que `AlertDialogDescription` rend un paragraphe, où une liste est invalide.
   */
  details?: ReactNode;
  /** Absents ensemble : le dialogue n'a que « Fermer ». */
  confirmLabel?: string;
  onConfirm?: () => void;
  testId: string;
  /**
   * Identifiants des deux boutons. Par défaut dérivés de `testId` ; certains écrans les fixent
   * eux-mêmes parce que `docs/SCREENS.md` les nomme autrement, et ces noms sont contractuels.
   */
  confirmTestId?: string;
  cancelTestId?: string;
}) {
  const { t } = useTranslation();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testId}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {details}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={cancelTestId ?? `${testId}-cancel`}>
            {onConfirm === undefined ? t('common:actions.close') : t('common:actions.cancel')}
          </AlertDialogCancel>
          {onConfirm !== undefined && (
            <AlertDialogAction
              onClick={onConfirm}
              data-testid={confirmTestId ?? `${testId}-confirm`}
            >
              {confirmLabel}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
